import { setTag as sentrySetTag } from '@sentry/browser';
import { app, render } from 'engine/index';
import { rpc, State } from 'engine/rpc';
import { Html5Qrcode } from 'html5-qrcode';
import { prefersReducedMotion, qrCodeToId, qrToCode } from 'shared/util';
import { Icon } from './Icon';
import { ChevronRight } from 'lucide-static';
import type { GameId } from 'shared/modelTypes/index';
import { playGame } from 'src/main';
import { networkLanguageLoader, setLanguage } from 'shared/translate';

type LoginRes = {
	game: GameId;
	playerId: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	updates: any[];
};

export function Login({
	failed,
	error,
	onSuccess,
}: {
	failed?: boolean | undefined;
	error?: string | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSuccess: (res: LoginRes) => void;
}) {
	let qrReader: Html5Qrcode | undefined = undefined;

	const ScanQRButton = () => {
		const button = document.getElementById('qr-button');
		if (qrReader) {
			if (button) button.innerText = 'Scan QR code';
			qrReader?.stop();
			qrReader = undefined;
		} else {
			if (button) button.innerText = 'Stop';

			qrReader = new Html5Qrcode('qr-reader');

			qrReader
				?.start(
					{ facingMode: 'environment' }, // Camera config: environment = rear camera
					{
						qrbox: { width: 250, height: 250 },
						fps: 10,
					},
					async (qrCodeMessage) => {
						qrReader?.stop();
						const code = qrToCode(qrCodeMessage);
						const id = qrCodeToId(code);
						if (State.sessionId) {
							const res = await rpc.rejoinPlaySession.query({
								gameId: id.toString(),
								sessionId: State.sessionId,
								playerId: State.playerId,
							});
							if (res.success) {
								playGame(res.updates);
							}
						} else {
							doLogin(code, id);
						}
					},
					(_errorMessage) => {
						// console.warn(`QR scan error: ${errorMessage}`);
					},
				)
				.catch((err) => {
					qrReader = undefined;
					console.error(`Unable to start scanning: ${err}`);
					render(app, <Login onSuccess={onSuccess} failed={true} error={err} />);
				});
		}
	};

	const view: HTMLElement = (
		<div class="flex size-full h-screen items-center justify-center">
			<div stagger-target class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
				<img src="/logo.svg" class="size-24" alt="bedrijfshelden logo" />
				<h1 class="text-balance text-center text-3xl font-bold text-gray-900">Login</h1>
				<p class="text-pretty text-center text-gray-700">Scan de QR code op het bord om door te gaan.</p>
				<div id="qr-reader" class="my-4 w-full">
					{failed && (
						<h2 class="text-balance text-center text-xl font-bold text-theme-600">{error || 'Deze code is ongeldig'}</h2>
					)}
				</div>
				<button id="qr-button" class="rounded-md bg-theme-600 p-4 text-theme-50" on:click={ScanQRButton}>
					Scan QR code
				</button>
				<span>of vul code in</span>
				<form
					on:submit={(e: SubmitEvent) => {
						e.preventDefault();
						const formData = new FormData(e.target as HTMLFormElement);
						const code = (formData.get('login-code') ?? '') as string;
						const id = qrCodeToId(code);
						doLogin(code, id);
					}}
					class="group flex items-center rounded-full border border-gray-200 bg-gray-100 has-[input:focus-visible]:border-gray-300 has-[input:focus-visible]:bg-gray-50"
				>
					<input
						type="text"
						name="login-code"
						id="login-code"
						placeholder="Code"
						autocomplete="off"
						size="6"
						spellcheck="false"
						class="bg-transparent px-4 py-2 font-mono outline-none"
					/>
					<button class="rounded-full bg-gray-200 p-2">
						<Icon icon={ChevronRight} class="size-5 text-gray-500" />
					</button>
				</form>
				<span validation-error class="text-danger-600 empty:hidden"></span>
			</div>
		</div>
	);

	const validationError = view.querySelector('[validation-error]') as HTMLElement;
	const doLogin = async (code: string, gameId: number) => {
		const res = await tryLogin(code, gameId);
		if (!res.success) {
			validationError.innerText = res.message;
		} else {
			onSuccess(res);
		}
	};

	requestAnimationFrame(() => {
		(async () => {
			if (prefersReducedMotion()) return;

			let i = 0;
			const staggerContainer = view.querySelector('[stagger-target]') as HTMLElement;

			for (const child of staggerContainer.childNodes) {
				if (child instanceof HTMLElement) {
					child.style.opacity = '0';
					child
						.animate(
							[
								{ transform: 'translateY(6rem)', opacity: '0' },
								{ transform: 'translateY(0rem)', opacity: '1' },
							],
							{
								duration: 1200,
								delay: 70 * i,
								easing: 'cubic-bezier(0.000, 0.980, 0.745, 1.000)',
							},
						)
						.finished.then(() => (child.style.opacity = ''));
					i++;
				}
			}
		})();
	});

	return view;
}

export async function tryLogin(
	code: string,
	gameId: number,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<{ success: false; message: string } | { success: true; playerId: string; game: GameId; updates: any[] }> {
	const res = await rpc.joinPlaySession.query({ code: code, id: gameId });
	if (!res.success) {
		return { success: false, message: res.error };
	}
	await setLanguage(res.language, networkLanguageLoader);

	State.gameId = res.gameId;
	State.playerId = res.playerId;
	State.gameToPlay = res.game;
	State.sessionId = res.sessionId;

	sentrySetTag('licenseCode', code);
	sentrySetTag('gameId', String(res.gameId));
	sentrySetTag('sessionId', res.sessionId);

	// console.log('your id is', res.playerId);
	return res;
}

export function signOut() {
	localStorage.removeItem('player-state');
	window.location.reload();
}
